"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import react, { useState } from "react";
import { useTranslation } from "@crehana/i18n";
import { cn } from "../../../utils.js";
import i18n from "../../i18n/index.js";
import { PrimitiveDropdown, PrimitiveDropdownApiEmpty, PrimitiveDropdownApiSearch, PrimitiveDropdownContent, PrimitiveDropdownList, PrimitiveDropdownLoading, PrimitiveDropdownTrigger, PrimitiveDropdownValueButton } from "../../primitive-dropdown/primitive-dropdown.js";
function AsyncGenericDropdown({ options, placeholder, open: controlledOpen, keepOpenOnSelect, className, dropdownContentClassName, disabled, size, removePortal, onOpenChange: controlledOnOpenChange, defaultSelected = [], onSearch, loading, searchProps, hasMore, onSearchMore, multiline, ...otherProps }) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;
    const [uncontrolledValues, setUncontrolledValues] = useState(Array.isArray(defaultSelected) ? defaultSelected : [
        defaultSelected
    ]);
    const open = controlledOpen ?? uncontrolledOpen;
    const handleOpenChange = (open)=>{
        onSearch('');
        onOpenChange(open);
    };
    if (!otherProps.multiselect) {
        const selectedValue = otherProps.selected ?? uncontrolledValues.at(0);
        const { allowDeselect, onSelect: controlledOnSelected } = otherProps;
        return /*#__PURE__*/ jsxs(PrimitiveDropdown, {
            open: open,
            onOpenChange: handleOpenChange,
            children: [
                /*#__PURE__*/ jsx(PrimitiveDropdownTrigger, {
                    asChild: true,
                    children: otherProps.customTriggerButton ?? /*#__PURE__*/ jsx(PrimitiveDropdownValueButton, {
                        className: className,
                        size: size,
                        disabled: disabled,
                        open: open,
                        placeholder: placeholder,
                        value: selectedValue?.label
                    })
                }),
                /*#__PURE__*/ jsxs(PrimitiveDropdownContent, {
                    removePadding: true,
                    removePortal: removePortal,
                    className: cn(multiline && 'tw-max-w-[500px]', dropdownContentClassName),
                    children: [
                        /*#__PURE__*/ jsx(SearchComponents, {
                            options: options,
                            onSearch: onSearch,
                            loading: loading,
                            searchProps: searchProps
                        }),
                        loading ? /*#__PURE__*/ jsx(PrimitiveDropdownLoading, {
                            loading: loading
                        }) : /*#__PURE__*/ jsx(PrimitiveDropdownList, {
                            children: options.map(({ element: Element, value, label }, index)=>{
                                const ListElement = /*#__PURE__*/ jsx(Element, {
                                    disabled: disabled,
                                    selected: selectedValue?.value === value,
                                    value: value,
                                    onSelect: ()=>{
                                        if (!keepOpenOnSelect) handleOpenChange(false);
                                        const valueToPropagate = allowDeselect && selectedValue?.value === value ? void 0 : {
                                            value: value,
                                            label
                                        };
                                        if (controlledOnSelected) {
                                            if (allowDeselect) controlledOnSelected(valueToPropagate);
                                            if (!allowDeselect && valueToPropagate) controlledOnSelected(valueToPropagate);
                                        }
                                        setUncontrolledValues(valueToPropagate ? [
                                            valueToPropagate
                                        ] : []);
                                    }
                                }, value);
                                return options.length - 1 === index && onSearchMore && hasMore ? /*#__PURE__*/ jsx(FetchMoreTrigger, {
                                    onSearchMore: onSearchMore,
                                    children: ListElement
                                }) : ListElement;
                            })
                        })
                    ]
                })
            ]
        });
    }
    const values = otherProps.selected ?? uncontrolledValues;
    return /*#__PURE__*/ jsxs(PrimitiveDropdown, {
        open: open,
        onOpenChange: handleOpenChange,
        children: [
            /*#__PURE__*/ jsx(PrimitiveDropdownTrigger, {
                asChild: true,
                children: otherProps.customTriggerButton ?? /*#__PURE__*/ jsx(PrimitiveDropdownValueButton, {
                    className: className,
                    size: size,
                    disabled: disabled,
                    open: open,
                    placeholder: placeholder,
                    value: values.map((value)=>value.label).join(', ')
                })
            }),
            /*#__PURE__*/ jsxs(PrimitiveDropdownContent, {
                removePadding: true,
                removePortal: removePortal,
                className: cn(multiline && 'tw-max-w-[500px]', dropdownContentClassName),
                children: [
                    /*#__PURE__*/ jsx(SearchComponents, {
                        options: options,
                        onSearch: onSearch,
                        loading: loading,
                        searchProps: searchProps
                    }),
                    loading ? /*#__PURE__*/ jsx(PrimitiveDropdownLoading, {
                        loading: loading
                    }) : /*#__PURE__*/ jsx(PrimitiveDropdownList, {
                        children: options.map(({ element: Element, value, label }, index)=>{
                            const ListElement = /*#__PURE__*/ jsx(Element, {
                                disabled: disabled,
                                selected: !!values.find((selected)=>selected.value === value),
                                value: value,
                                onSelect: ()=>{
                                    const valuesToPropagate = values.find((selected)=>selected.value === value) ? values.filter((selected)=>selected.value !== value) : [
                                        ...values,
                                        {
                                            value,
                                            label
                                        }
                                    ];
                                    if (otherProps.onSelect) otherProps.onSelect(valuesToPropagate);
                                    setUncontrolledValues(valuesToPropagate);
                                }
                            }, value);
                            return options.length - 1 === index && onSearchMore && hasMore ? /*#__PURE__*/ jsx(FetchMoreTrigger, {
                                onSearchMore: onSearchMore,
                                children: ListElement
                            }) : ListElement;
                        })
                    })
                ]
            })
        ]
    });
}
function SearchComponents({ loading, onSearch, options, searchProps }) {
    const { languageCode } = useTranslation();
    const searchPlaceholder = searchProps?.searchPlaceholder ?? i18n[languageCode].DROPDOWN_SEARCH_PLACEHOLDER;
    const emptyViewTitle = searchProps?.noFoundPlaceholderTitle ?? i18n[languageCode].DROPDOWN_EMPTY_TITLE;
    const emptyViewSubtitle = searchProps?.noFoundPlaceholderSubtitle ?? i18n[languageCode].DROPDOWN_EMPTY_SUBTITLE;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(PrimitiveDropdownApiSearch, {
                placeholder: searchPlaceholder,
                onSearch: onSearch
            }),
            !loading && 0 === options.length && /*#__PURE__*/ jsx(PrimitiveDropdownApiEmpty, {
                title: emptyViewTitle,
                subtitle: emptyViewSubtitle
            })
        ]
    });
}
function FetchMoreTrigger({ onSearchMore, children }) {
    const fetchMoreRef = react.useRef(null);
    react.useEffect(()=>{
        if (!fetchMoreRef.current) return;
        const observer = new IntersectionObserver((entries)=>{
            if (entries[0].isIntersecting) onSearchMore();
        });
        observer.observe(fetchMoreRef.current);
        return ()=>{
            observer.disconnect();
        };
    }, [
        fetchMoreRef,
        onSearchMore
    ]);
    return /*#__PURE__*/ jsx("div", {
        ref: fetchMoreRef,
        children: children
    });
}
export { AsyncGenericDropdown };
